import client from '../../../config/api';
import DIJVueConfigurations from '../../../helpers/DIJVueConfigurations';

import {
  deleteRecord,
  getDirtySyncRecords,
  getRecord,
  getRecordUsingSync,
  updateRecord,
} from '../../../services/db';

import { ADD_SYNCHRONIZED_BUBBLE } from '../../bubbles/mutations/mutationTypes';

import {
  CREATE_QUESTION,
  DELETE_QUESTION,
  UPDATE_QUESTION,
} from '../../questions/actions/actionTypes';

const synchronizeJob = (syncRecord, job) => {
  switch (syncRecord.status) {
    case 'new':
      return client.post('job/', job);
    case 'existing':
      return client.put(`job/${job._id}`, job);
    case 'deleted':
      return client.delete(`job/${job._id}`);
    default:
      throw new Error(`Unsupported status '${syncRecord.status}'`);
  }
};

const synchronizeTask = (syncRecord, task) => {
  switch (syncRecord.status) {
    case 'new':
      return client.post('task/', task);
    case 'existing':
      return client.put(`/task/${task._id}`, task);
    case 'deleted':
      return client.delete(`/task/${task._id}`);
    default:
      throw new Error(`Unsupported status '${syncRecord.status}'`);
  }
};

const synchronizeBubble = async (syncRecord, bubble, commit) => {
  const uploadPath = await (async () => {
    const task = await getRecord('task', bubble.taskId);
    const job = task?.jobId ? await getRecord('job', task.jobId) : null;
    const sessionUser = await getRecord('session', 'user');

    const rawLocation = sessionUser?.location || '';
    const site = rawLocation.substring(0, 3) || 'UNK';
    const jobName = job?.name || 'offline-job';

    return `uploads/sites/${site}/${jobName}/${bubble.taskId}`;
  })();

  if (Array.isArray(bubble.images) && bubble.images.length > 0) {
    const dijConfigurations = new DIJVueConfigurations();

    bubble.images = await Promise.all(
      bubble.images.map(async (image, i) => {
        const imagePath = image.url || image.src || image.path || '';
        const isLocalDataUrl =
          typeof imagePath === 'string' && imagePath.startsWith('data:');

        if (!isLocalDataUrl) {
          return image;
        }

        const response = await fetch(imagePath);
        const blob = await response.blob();

        const fallbackName = `bubble-${bubble._id || bubble.taskId}-${
          i + 1
        }.jpg`;
        const fileName = image.name || fallbackName;
        const file = new File([blob], fileName, { type: blob.type });

        const uploadedUrl = await dijConfigurations.uploadFileToS3(
          file,
          fileName,
          uploadPath
        );

        const updated = { ...image, url: uploadedUrl };
        delete updated.src;
        delete updated.path;
        return updated;
      })
    );
  }

  let response;
  switch (syncRecord.status) {
    case 'new':
      response = await client.post(`bubble/${bubble.taskId}`, bubble);
      commit(ADD_SYNCHRONIZED_BUBBLE, response.data, { root: true });
      return response;

    case 'existing':
      response = await client.put(`bubble/${bubble._id}`, bubble);
      commit(ADD_SYNCHRONIZED_BUBBLE, response.data, { root: true });
      return response;

    case 'deleted':
      return client.delete(`bubble/${bubble._id}`);

    default:
      throw new Error(`Unsupported status '${syncRecord.status}'`);
  }
};

const synchronizeQuestion = async (syncRecord, question, dispatch) => {
  const actionMap = {
    new: {
      type: CREATE_QUESTION,
      payload: { taskId: question.taskId, question },
    },
    existing: {
      type: UPDATE_QUESTION,
      payload: question,
    },
    deleted: { type: DELETE_QUESTION, payload: question._id },
  };

  const actionEntry = actionMap[syncRecord.status];

  if (!actionEntry) {
    throw new Error(`Unsupported status '${syncRecord.status}'`);
  }

  await dispatch(actionEntry.type, actionEntry.payload, { root: true });
};

const synchronizeImage = async (syncRecord, image) => {
  const { base64data, fileName, path } = image;
  const dijConfigurations = new DIJVueConfigurations();

  if (!path || typeof path !== 'string' || path.lastIndexOf('/') === -1) {
    console.warn(
      `[OfflineSync] Skipping image sync with invalid path for recordId=${syncRecord.recordId}`
    );
    return null;
  }

  let uploadPath = path.substring(0, path.lastIndexOf('/'));
  if (uploadPath.startsWith('/')) {
    uploadPath = uploadPath.substring(1);
  }
  const response = await fetch(base64data);
  const blob = await response.blob();
  const file = new File([blob], fileName, { type: blob.type });

  return dijConfigurations.uploadFileToS3(file, fileName, uploadPath);
};

export default {
  async SYNCHRONIZE_LOCAL_DATABASE(
    { dispatch, commit },
    { toast, manualSync = false }
  ) {
    const hasToast = !!toast;
    console.log(
      `[OfflineSync] Sync started. manualSync=${manualSync}, hasToast=${hasToast}`
    );

    // Fetch all dirty records
    const dirtySyncRecords = await getDirtySyncRecords(manualSync);
    const totalRecords = dirtySyncRecords.length;
    console.log(`[OfflineSync] Retrieved dirty sync records: ${totalRecords}`);

    if (totalRecords === 0) {
      console.log('[OfflineSync] Nothing to synchronize.');
      return;
    }

    // Create an initial toast notification
    const syncToast = hasToast
      ? toast.open({
          duration: 50000, // Keep toast open during sync
          message: 'Preparing to synchronize...',
          type: 'is-warning',
          position: 'is-bottom',
        })
      : null;

    let processedRecords = 0;

    // Function to update toast message
    const updateToastMessage = (successCount, failureCount) => {
      if (syncToast) {
        syncToast.message = `Sync Progress: ${processedRecords}/${totalRecords} | ✅ ${successCount} | ❌ ${failureCount}`;
      }
    };

    // Map over records and create promises for all sync operations
    const typeOrderForward = {
      job: 0,
      task: 1,
      question: 2,
      bubble: 3,
      image: 4,
    };

    const typeOrderDelete = {
      image: 0,
      bubble: 1,
      question: 2,
      task: 3,
      job: 4,
    };

    const orderedDirtySyncRecords = [...dirtySyncRecords].sort((a, b) => {
      const aOrder =
        a.status === 'deleted'
          ? typeOrderDelete[a.recordType] ?? 99
          : typeOrderForward[a.recordType] ?? 99;
      const bOrder =
        b.status === 'deleted'
          ? typeOrderDelete[b.recordType] ?? 99
          : typeOrderForward[b.recordType] ?? 99;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return (a.date || 0) - (b.date || 0);
    });

    const syncPromises = [];

    // Process records sequentially to preserve dependency order
    // eslint-disable-next-line no-restricted-syntax
    for (const dirtySyncRecord of orderedDirtySyncRecords) {
      // eslint-disable-next-line no-loop-func
      const syncPromise = (async () => {
        try {
          console.log(
            `[OfflineSync] Processing record type=${
              dirtySyncRecord.recordType
            } id=${dirtySyncRecord.recordId} status=${
              dirtySyncRecord.status
            } checkedJobId=${dirtySyncRecord.checkedJobId || '-'} isDirty=${
              dirtySyncRecord.isDirty
            }`
          );

          const dataRecord = await getRecordUsingSync(dirtySyncRecord);

          if (!dataRecord) {
            if (dirtySyncRecord.status === 'deleted') {
              console.log(
                `[OfflineSync] Sync record has no local data and is deleted. Removing sync tombstone id=${dirtySyncRecord._id}`
              );
              await deleteRecord('sync', dirtySyncRecord._id);
              return { status: 'fulfilled', record: dirtySyncRecord };
            }

            console.warn(
              `[OfflineSync] Skipping record (Type: ${dirtySyncRecord.recordType}, ID: ${dirtySyncRecord.recordId}) - No matching data found.`
            );
            return { status: 'skipped', record: dirtySyncRecord };
          }

          let syncOperation;
          switch (dirtySyncRecord.recordType) {
            case 'job':
              syncOperation = synchronizeJob(dirtySyncRecord, dataRecord);
              break;
            case 'task':
              syncOperation = synchronizeTask(dirtySyncRecord, dataRecord);
              break;
            case 'bubble':
              syncOperation = synchronizeBubble(
                dirtySyncRecord,
                dataRecord,
                commit
              );
              break;
            case 'question':
              syncOperation = synchronizeQuestion(
                dirtySyncRecord,
                dataRecord,
                dispatch,
                commit
              );
              break;
            case 'image':
              syncOperation = synchronizeImage(
                dirtySyncRecord,
                dataRecord,
                dispatch
              );
              break;
            default:
              throw new Error(
                `Unsupported type '${dirtySyncRecord.recordType}'`
              );
          }

          try {
            await syncOperation; // Await the sync operation
            console.log(
              `[OfflineSync] Synced record type=${dirtySyncRecord.recordType} id=${dirtySyncRecord.recordId} status=${dirtySyncRecord.status}`
            );
          } catch (error) {
            console.error(
              `[OfflineSync] Error synchronizing record (Type: ${dirtySyncRecord.recordType}, ID: ${dirtySyncRecord.recordId})`,
              error
            );
            throw error; // eslint-disable-line no-useless-catch
          }

          // After sync delete also the record in case of delete
          if (dirtySyncRecord.status === 'deleted') {
            console.log(
              `[OfflineSync] Removing deleted record from local DB type=${dirtySyncRecord.recordType} id=${dataRecord._id}`
            );
            await deleteRecord(dirtySyncRecord.recordType, dataRecord._id);
            await deleteRecord('sync', dirtySyncRecord._id);
          }

          let deleteCheckedJobId = false;
          if (dirtySyncRecord.checkedJobId && manualSync) {
            dirtySyncRecord.checkedJobId = null;
            deleteCheckedJobId = true;
          }

          // set isDirty back to false after successful sync and delete checkedJobId
          if (dirtySyncRecord.status !== 'deleted') {
            await updateRecord(
              dirtySyncRecord.recordType,
              dataRecord,
              null,
              deleteCheckedJobId,
              false
            );
            console.log(
              `[OfflineSync] Marked clean type=${dirtySyncRecord.recordType} id=${dirtySyncRecord.recordId}`
            );
          }

          return { status: 'fulfilled', record: dirtySyncRecord };
        } catch (error) {
          console.error(
            `[OfflineSync] Failed to sync record (Type: ${dirtySyncRecord.recordType}, ID: ${dirtySyncRecord.recordId})`,
            error
          );
          throw error;
        }
      })();

      syncPromises.push(syncPromise);

      try {
        // eslint-disable-next-line no-await-in-loop
        await syncPromise;
      } catch (error) {
        // Keep iterating to process remaining records; final outcome is reported by allSettled below.
      }
    }

    // Wait for all sync requests to complete
    const results = await Promise.allSettled(syncPromises);

    // Count success and failed requests
    const successCount = results.filter(
      (result) => result.status === 'fulfilled'
    ).length;
    const failureCount = results.filter(
      (result) => result.status === 'rejected'
    ).length;

    console.log(
      `[OfflineSync] Completed. total=${totalRecords}, success=${successCount}, failed=${failureCount}`
    );

    processedRecords = successCount + failureCount;
    updateToastMessage(successCount, failureCount);

    if (syncToast) {
      if (failureCount > 0) {
        syncToast.message = `Sync Completed: ${successCount} | ${failureCount} failed. Check console for details.`;
        syncToast.type = 'is-danger';
      } else {
        syncToast.message = `Synchronization Complete: ${successCount} records processed successfully.`;
        syncToast.type = 'is-success';
      }

      setTimeout(() => {
        syncToast.close();
      }, 3000);
    }
  },
};
